/**
 * 语音对话控制器（T10）：把 TTS 播放、录音转写、语音模式开关串成一个可测对象。
 *
 * 不依赖 React：组件通过 `useVoiceController()` 订阅快照；测试可直接 `new` 它，
 * 注入 fake 适配器 / 录音器，验证「录音 → 转写 → 填入输入框」与语音模式回路。
 *
 * 语音对话模式（`t10.voiceMode`）开启后：
 * 1. 助手回复落库后自动 TTS 朗读（`autoSpeakAssistant`）；
 * 2. 朗读自然结束后自动重新开始录音，形成免手动回路（录音器可用时）。
 */

import type { TtsRequest, TtsResult } from '../../core/tts';
import { audioDuration } from './audioDuration';
import { createConfigIssue, toVoiceIssue } from './issues';
import { TtsPlayer } from './playback';
import type { TtsPlayerSnapshot } from './playback';
import type { ReadableStorage, RecordingState, VoiceDeps, VoiceIssue } from './types';
import { readVoiceMode, writeVoiceMode } from './voiceMode';

export interface VoiceSnapshot {
  /** 语音对话模式开关。 */
  voiceMode: boolean;
  /** TTS 播放状态（四态）。 */
  playback: TtsPlayerSnapshot;
  /** 录音 / 转写状态。 */
  recording: { state: RecordingState; issue: VoiceIssue | null };
}

export interface VoiceControllerOptions {
  deps: VoiceDeps;
  /** 语音模式持久化存储；缺省 `localStorage`。 */
  storage?: ReadableStorage;
  /** 转写成功后的回调（ChatPage 用它把文本填入输入框，不自动发送）。 */
  onTranscript?: (text: string) => void;
  /** 结构化错误回调（可选，用于统一 toast）。 */
  onIssue?: (issue: VoiceIssue) => void;
}

const IDLE_RECORDING: VoiceSnapshot['recording'] = { state: 'idle', issue: null };

export class VoiceController {
  private readonly deps: VoiceDeps;
  private readonly storage: ReadableStorage | undefined;
  private readonly onTranscriptCallback: ((text: string) => void) | undefined;
  private readonly onIssueCallback: ((issue: VoiceIssue) => void) | undefined;
  private readonly player: TtsPlayer;
  private readonly listeners = new Set<() => void>();
  private readonly prepared = new Map<string, { text: string; result: TtsResult }>();
  private readonly preparing = new Map<string, { text: string; job: Promise<TtsResult> }>();
  private readonly preparationAborts = new Set<AbortController>();
  private audioEpoch = 0;

  private voiceMode: boolean;
  private playback: TtsPlayerSnapshot;
  private recording: VoiceSnapshot['recording'] = { ...IDLE_RECORDING };

  constructor(options: VoiceControllerOptions) {
    this.deps = options.deps;
    this.storage = options.storage;
    this.onTranscriptCallback = options.onTranscript;
    this.onIssueCallback = options.onIssue;
    this.voiceMode = readVoiceMode(this.storage);
    this.player = new TtsPlayer({
      adapter: this.deps.tts ? {
        id: this.deps.tts.id, kind: this.deps.tts.kind,
        listVoices: () => this.deps.tts!.listVoices(),
        speak: req => req.messageId ? this.prepareAudio(req.messageId, req) : this.deps.tts!.speak(req),
      } : null,
      ...(this.deps.audioFactory === undefined ? {} : { audioFactory: this.deps.audioFactory }),
      ...(this.deps.objectUrl === undefined ? {} : { objectUrl: this.deps.objectUrl }),
      onChange: (snapshot) => {
        this.playback = snapshot;
        if (snapshot.issue !== null) this.onIssueCallback?.(snapshot.issue);
        this.emit();
      },
      onEnded: () => {
        this.handlePlaybackEnded();
      },
    });
    this.playback = this.player.getSnapshot();
  }

  getSnapshot(): VoiceSnapshot {
    return {
      voiceMode: this.voiceMode,
      playback: this.playback,
      recording: this.recording,
    };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** 设置语音对话模式并持久化（键 `t10.voiceMode`）。 */
  setVoiceMode(enabled: boolean): void {
    if (this.voiceMode === enabled) return;
    this.voiceMode = enabled;
    writeVoiceMode(enabled, this.storage);
    this.emit();
  }

  toggleVoiceMode(): void {
    this.setVoiceMode(!this.voiceMode);
  }

  /** 播放某条消息的 TTS；失败进入结构化错误态，不抛出。 */
  async playMessage(messageId: string, req: TtsRequest): Promise<void> {
    await this.player.play(messageId, req);
  }

  async playAudioMessage(messageId: string, source: string): Promise<void> {
    await this.player.playSource(messageId, source);
  }

  seekPlayback(seconds: number): void {
    this.player.seek(seconds);
  }

  private async prepareAudio(messageId: string, req: TtsRequest): Promise<TtsResult> {
    req = { ...req, text: req.text.trim() };
    const cached = this.prepared.get(messageId);
    if (cached?.text === req.text) return cached.result;
    const pending = this.preparing.get(messageId);
    if (pending?.text === req.text) return pending.job;
    if (!this.deps.tts) throw new Error('语音未配置');
    const epoch = this.audioEpoch;
    const abort = new AbortController();
    this.preparationAborts.add(abort);
    const cancel = () => abort.abort();
    req.signal?.addEventListener('abort', cancel, { once: true });
    if (req.signal?.aborted) cancel();
    const timer = setTimeout(cancel, 90000);
    let rejectCancelled: () => void = () => undefined;
    const cancelled = new Promise<never>((_, reject) => {
      rejectCancelled = () => reject(new DOMException('Aborted', 'AbortError'));
      abort.signal.addEventListener('abort', rejectCancelled, { once: true });
    });
    const job = (async () => {
      try {
        if (abort.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const result = await Promise.race([this.deps.tts!.speak({ ...req, messageId, signal: abort.signal }), cancelled]);
        if (epoch !== this.audioEpoch || abort.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        this.prepared.set(messageId, { text: req.text, result });
        if (this.prepared.size > 16) this.prepared.delete(this.prepared.keys().next().value!);
        return result;
      } finally {
        clearTimeout(timer);
        abort.signal.removeEventListener('abort', rejectCancelled);
        req.signal?.removeEventListener('abort', cancel);
        this.preparationAborts.delete(abort);
        if (epoch === this.audioEpoch) this.preparing.delete(messageId);
      }
    })();
    this.preparing.set(messageId, { text: req.text, job });
    return job;
  }

  /** Synthesize once without starting playback, so text can wait for saved audio. */
  async prepareMessage(messageId: string, text: string): Promise<number | null> {
    const epoch = this.audioEpoch;
    const result = await this.prepareAudio(messageId, { text });
    const duration = await audioDuration(result.audio);
    if (epoch !== this.audioEpoch) throw new DOMException('Aborted', 'AbortError');
    return duration;
  }

  clearPreparedAudio(): void {
    this.audioEpoch++;
    for (const abort of this.preparationAborts) abort.abort();
    this.preparing.clear();
    this.prepared.clear();
    this.stopPlayback();
  }

  stopPlayback(): void {
    this.player.stop();
  }

  dismissPlaybackIssue(): void {
    this.player.dismiss();
  }

  /**
   * 语音对话模式开启时，自动朗读刚落库的助手回复。
   * 关闭模式 / 空文本时是 no-op，因此不改变既有对话行为。
   */
  async autoSpeakAssistant(messageId: string, text: string): Promise<void> {
    if (!this.voiceMode) return;
    const trimmed = text.trim();
    if (trimmed === '') return;
    await this.playMessage(messageId, { text: trimmed });
  }

  /** 开始录音；不支持录音 / 授权失败时给出结构化错误。 */
  async startRecording(): Promise<void> {
    if (this.recording.state === 'recording' || this.recording.state === 'transcribing') return;
    const recorder = this.deps.recorder;
    if (recorder === null) {
      this.setRecording('error', createConfigIssue('recorder'));
      return;
    }
    try {
      await recorder.start();
    } catch (error) {
      this.setRecording('error', toVoiceIssue(error, 'recorder'));
      return;
    }
    this.setRecording('recording', null);
  }

  /**
   * 结束录音 → 调用 `src/core/voice` 转写 → 把文本交给 `onTranscript`
   * （填入输入框，不自动发送）。返回转写文本，失败返回 null。
   */
  async stopRecording(): Promise<string | null> {
    const recorder = this.deps.recorder;
    if (recorder === null) {
      this.setRecording('error', createConfigIssue('recorder'));
      return null;
    }
    if (this.recording.state !== 'recording') return null;

    this.setRecording('transcribing', null);

    let audio: Blob;
    try {
      audio = await recorder.stop();
    } catch (error) {
      this.setRecording('error', toVoiceIssue(error, 'recorder'));
      return null;
    }

    const adapter = this.deps.transcribe;
    if (adapter === null) {
      this.setRecording('error', createConfigIssue('transcribe'));
      return null;
    }

    let text: string;
    try {
      const result = await adapter.transcribe({ audio });
      text = result.text.trim();
    } catch (error) {
      this.setRecording('error', toVoiceIssue(error, 'transcribe'));
      return null;
    }

    this.setRecording('idle', null);
    if (text !== '') this.onTranscriptCallback?.(text);
    return text;
  }

  /** 放弃当前录音（不转写）。 */
  cancelRecording(): void {
    try {
      this.deps.recorder?.cancel();
    } catch {
      // 取消失败不影响状态复位
    }
    this.setRecording('idle', null);
  }

  /** 组件卸载：释放播放资源（可重复调用）。 */
  dispose(): void {
    this.clearPreparedAudio();
    this.player.dispose();
    this.listeners.clear();
  }

  private handlePlaybackEnded(): void {
    if (!this.voiceMode) return;
    if (this.deps.recorder === null) return;
    if (this.recording.state !== 'idle') return;
    void this.startRecording();
  }

  private setRecording(state: RecordingState, issue: VoiceIssue | null): void {
    this.recording = { state, issue };
    if (issue !== null) this.onIssueCallback?.(issue);
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
